import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { format, parseISO } from 'date-fns';
import { BPReading } from '../utils/api';

interface BPChartProps {
  readings: BPReading[];
}

interface ChartDataPoint {
  date: string;
  systolic: number;
  diastolic: number;
  timestamp: string;
  heartRate?: number;
}

const BPChart: React.FC<BPChartProps> = ({ readings }) => {
  const [timeFilter, setTimeFilter] = useState<'morning' | 'evening' | 'all'>('all');

  // Prepare chart data
  const chartData: ChartDataPoint[] = readings
    .slice()
    .reverse() // Show chronological order (oldest to newest)
    .map(reading => {
      const timestamp = reading.timestamp ? parseISO(reading.timestamp) : new Date();
      return {
        date: format(timestamp, 'MMM dd'),
        systolic: reading.systolic,
        diastolic: reading.diastolic,
        timestamp: reading.timestamp || '',
        heartRate: reading.heart_rate
      };
    })
    .filter(data => {
      if (timeFilter === 'all') return true;
      
      const hour = parseISO(data.timestamp).getHours();
      if (timeFilter === 'morning') return hour >= 6 && hour < 12;
      if (timeFilter === 'evening') return hour >= 18 || hour < 6;
      return true;
    });

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{label}</p>
          <p className="text-chart-red">
            <span className="inline-block w-3 h-3 bg-chart-red rounded-full mr-2"></span>
            Systolic: {data.systolic} mmHg
          </p>
          <p className="text-chart-blue">
            <span className="inline-block w-3 h-3 bg-chart-blue rounded-full mr-2"></span>
            Diastolic: {data.diastolic} mmHg
          </p>
          {data.heartRate && (
            <p className="text-gray-600">
              Heart Rate: {data.heartRate} bpm
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  if (readings.length === 0) {
    return (
      <div className="card">
        <h3 className="section-header">Your trend</h3>
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <p className="text-lg font-medium mb-2">No data yet</p>
            <p className="text-sm">Add some BP readings to see your trend</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <h3 className="section-header mb-0">Your trend</h3>
        
        {/* Time filter toggle */}
        <div className="flex items-center bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setTimeFilter('morning')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              timeFilter === 'morning' 
                ? 'bg-white text-gray-800 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Morning
          </button>
          <button
            onClick={() => setTimeFilter('evening')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ml-1 ${
              timeFilter === 'evening' 
                ? 'bg-gray-800 text-white shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Evening
          </button>
          <button
            onClick={() => setTimeFilter('all')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ml-1 ${
              timeFilter === 'all' 
                ? 'bg-white text-gray-800 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            All
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="h-64 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="date" 
              stroke="#6B7280"
              fontSize={12}
            />
            <YAxis 
              stroke="#6B7280"
              fontSize={12}
              domain={['dataMin - 10', 'dataMax + 10']}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
            />
            <Line 
              type="monotone" 
              dataKey="systolic" 
              stroke="#DC3545" 
              strokeWidth={2}
              dot={{ fill: '#DC3545', r: 4 }}
              name="Systolic"
            />
            <Line 
              type="monotone" 
              dataKey="diastolic" 
              stroke="#007BFF" 
              strokeWidth={2}
              dot={{ fill: '#007BFF', r: 4 }}
              name="Diastolic"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center space-x-6 text-sm">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-chart-red rounded-full mr-2"></div>
          <span className="text-gray-600">Systolic</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-chart-blue rounded-full mr-2"></div>
          <span className="text-gray-600">Diastolic</span>
        </div>
      </div>
    </div>
  );
};

export default BPChart;